import { useOrganizationId } from '@features/user';
import { Alert, Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';

import { useGetRequestCopyPreviewQuery } from '../store/api';

export interface ComposedRequestMessage {
  smsBody: string;
  emailSubject: string;
  emailBody: string;
}

export interface RequestMessagePreviewViewProps {
  message?: ComposedRequestMessage;
  isLoading?: boolean;
  isError?: boolean;
  /** "Edit message" for an admin who may change it; "View message" otherwise. */
  editLabel: string;
  onEditMessage: () => void;
}

/**
 * The text and email a patient receives for ANY requirement (GDN-4141,
 * GDN-4142), shown inside each requirement's "What patients see".
 *
 * The practice has one request message. The server composes it without any
 * requirement field (`request_copy_service.py`), so the preview says so in so
 * many words and links to the one place it is edited, rather than letting the
 * row suggest a per-requirement message the product does not have.
 */
export const RequestMessagePreviewView = ({
  message,
  isLoading = false,
  isError = false,
  editLabel,
  onEditMessage,
}: RequestMessagePreviewViewProps) => (
  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
    <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
      <Typography variant="caption" color="text.secondary">
        Same message for every requirement ·
      </Typography>
      <Button
        size="small"
        variant="text"
        onClick={onEditMessage}
        sx={{ textTransform: 'none', minWidth: 0, p: 0, fontSize: 12 }}
      >
        {editLabel}
      </Button>
    </Stack>
    {isLoading ? (
      <Stack spacing={1}>
        <Skeleton variant="text" />
        <Skeleton variant="rounded" height={72} />
      </Stack>
    ) : isError || !message ? (
      <Alert severity="warning" variant="outlined" sx={{ py: 0 }}>
        The message could not be loaded.
      </Alert>
    ) : (
      <>
        <Typography variant="caption" color="text.secondary">
          Text message
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.25, overflowWrap: 'anywhere' }}>
          {message.smsBody}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Email
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {message.emailSubject}
        </Typography>
        <Box>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
            {message.emailBody}
          </Typography>
        </Box>
      </>
    )}
  </Paper>
);

/**
 * The saved message, composed by the server's preview endpoint — the same one
 * the send preview reads, so the two cannot show different text.
 */
export const RequestMessagePreview = ({
  editLabel,
  onEditMessage,
}: Pick<RequestMessagePreviewViewProps, 'editLabel' | 'onEditMessage'>) => {
  const organizationId = useOrganizationId();
  const { data, isLoading, isError } = useGetRequestCopyPreviewQuery(
    { orgId: organizationId ?? '' },
    { skip: !organizationId },
  );
  return (
    <RequestMessagePreviewView
      message={
        data
          ? {
              smsBody: data.sms.body,
              emailSubject: data.email.subject,
              emailBody: data.email.body,
            }
          : undefined
      }
      isLoading={isLoading}
      isError={isError}
      editLabel={editLabel}
      onEditMessage={onEditMessage}
    />
  );
};
